import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { loadUserPreferences } from 'graphql/preferences/queries';
import { setPreference } from 'graphql/preferences/mutations';
import { createPreferenceWriter } from 'contexts/preferences-context/writer';
import { useUserInformationContext } from 'contexts/userContext';
import { getValidAccessToken } from 'services/auth';
import i18n from 'i18n';
import { MutateThemeContext } from 'providers/mutate-theme-provider';
import { logger } from 'utils/logger';

/**
 * @typedef {Object} RecentOrder
 * @property {string} id - Order ID
 * @property {string} orderId - Order code/number
 * @property {string} firstPropertyAddress - Primary property address
 * @property {number} lastSeen - Timestamp (ms) of last activation
 * @property {string} [code] - Alias for orderId (backward compat)
 */

/**
 * @typedef {Object} PreferencesContextValue
 * @property {string} selectedAgencyId
 * @property {string} ordersView
 * @property {string[]} orderColumns
 * @property {RecentOrder[]} recentOrders
 * @property {boolean} orderMenuCollapsed
 * @property {boolean} assistantFullscreen
 * @property {string} language
 * @property {boolean} isReady
 * @property {boolean} isLoading
 * @property {string|null} error
 * @property {(value: string) => void} setSelectedAgencyId
 * @property {(value: string) => void} setOrdersView
 * @property {(value: string[]) => void} setOrderColumns
 * @property {(value: boolean) => void} setOrderMenuCollapsed
 * @property {(value: boolean) => void} setAssistantFullscreen
 * @property {(value: string) => void} setLanguage
 * @property {(order: RecentOrder) => void} activateOrder
 * @property {(id: string) => void} removeRecentOrder
 * @property {(id: string, address: string) => void} updateRecentOrderAddress
 */

/**
 * PreferencesContext
 *
 * Manages user preferences loaded from backend via GraphQL.
 * All 6 preferences are stored as JSON-encoded strings on the backend.
 * Each is parsed individually with try/catch to isolate failures.
 *
 * Defaults (when null or parse fails):
 *   - selectedAgencyId: "" (empty string)
 *   - ordersView: "orders-map"
 *   - orderColumns: ["order_id","agency","address","opener","seller","buyer","transaction_type","type","closing_date","purchase_price","loan_amount"]
 *   - recentOrders: [] (empty array)
 *   - orderMenuCollapsed: true (boolean)
 *   - assistantFullscreen: false (boolean)
 *   - language: "en" (strict enum: "en" or "es")
 *
 * Legacy migration:
 *   - recentOrders accepts both "lastSeen" (canonical) and "lastOpened" (legacy).
 *     If lastSeen is null, falls back to lastOpened, then to 0.
 *   - lastSeen is parsed as a number (milliseconds since epoch).
 *
 * Validation:
 *   - selectedAgencyId is validated against currentUser.accesses[].id.
 *     If not found, reset to "".
 *
 * State management:
 *   - isReady: false until first load completes. Gates writer enqueue.
 *   - isLoading: true while fetching preferences.
 *   - error: null or error message from GraphQL.
 *
 * No localStorage reads/writes for these 6 keys (except legacy migration in Task 6).
 */

const DEFAULT_SELECTED_AGENCY_ID = '';
const DEFAULT_ORDERS_VIEW = 'orders-map';
const DEFAULT_ORDER_COLUMNS = [
  'order_id',
  'agency',
  'address',
  'opener',
  'seller',
  'buyer',
  'transaction_type',
  'type',
  'closing_date',
  'purchase_price',
  'loan_amount',
];
const DEFAULT_RECENT_ORDERS = [];
const DEFAULT_ORDER_MENU_COLLAPSED = true;
const DEFAULT_ASSISTANT_FULLSCREEN = false;
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_THEME = 'light';

const PreferencesContext = createContext(null);

export const PreferencesProvider = ({ children }) => {
  const { currentUser, token } = useUserInformationContext();
  const [selectedAgencyId, setSelectedAgencyId] = useState(DEFAULT_SELECTED_AGENCY_ID);
  const [ordersView, setOrdersView] = useState(DEFAULT_ORDERS_VIEW);
  const [orderColumns, setOrderColumns] = useState(DEFAULT_ORDER_COLUMNS);
  const [recentOrders, setRecentOrders] = useState(DEFAULT_RECENT_ORDERS);
  const [orderMenuCollapsed, setOrderMenuCollapsed] = useState(DEFAULT_ORDER_MENU_COLLAPSED);
  const [assistantFullscreen, setAssistantFullscreen] = useState(DEFAULT_ASSISTANT_FULLSCREEN);
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [theme, setThemeState] = useState(DEFAULT_THEME);
  const { setTheme: setAppTheme } = useContext(MutateThemeContext);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Preference writer instance (created once, persists across renders)
  const writerRef = useRef(null);
  if (writerRef.current === null) {
    writerRef.current = createPreferenceWriter({ savePreference: setPreference });
  }
  const writer = writerRef.current;

  /**
	 * Safe JSON parse with fallback.
	 * Returns parsed value or fallback if null/parse fails.
	 */
  const safeJsonParse = (jsonString, fallback) => {
    if (jsonString === null || jsonString === undefined) {
      return fallback;
    }
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      logger.warn('JSON parse failed for preference:', jsonString, e);
      return fallback;
    }
  };

  /**
	 * Load preferences on mount when authenticated.
	 * Imperative call to loadUserPreferences (not useQuery).
	 * Gated on the managed OAuth session (getValidAccessToken) before the first call.
	 */
  useEffect(() => {
    const loadPreferences = async () => {
      // Auth gate: the managed OAuth session must yield a valid access token
      try {
        await getValidAccessToken();
      } catch (_) {
        setIsReady(true);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const prefs = await loadUserPreferences();

        // Parse each preference individually with try/catch
        const parsedSelectedAgencyId = safeJsonParse(
          prefs.selectedAgencyId,
          DEFAULT_SELECTED_AGENCY_ID
        );
        const parsedOrdersView = safeJsonParse(
          prefs.ordersView,
          DEFAULT_ORDERS_VIEW
        );
        const parsedOrderColumns = safeJsonParse(
          prefs.orderColumns,
          DEFAULT_ORDER_COLUMNS
        );
        const parsedRecentOrders = safeJsonParse(
          prefs.recentOrders,
          DEFAULT_RECENT_ORDERS
        );
        const parsedOrderMenuCollapsed = safeJsonParse(
          prefs.orderMenuCollapsed,
          DEFAULT_ORDER_MENU_COLLAPSED
        );
        const parsedAssistantFullscreen = safeJsonParse(
          prefs.assistantFullscreen,
          DEFAULT_ASSISTANT_FULLSCREEN
        );
        const parsedLanguage = safeJsonParse(
          prefs.language,
          DEFAULT_LANGUAGE
        );
        const parsedTheme = safeJsonParse(prefs.theme, DEFAULT_THEME);
        const validatedTheme = parsedTheme === 'dark' || parsedTheme === 'light' ? parsedTheme : DEFAULT_THEME;

        // Validate language: strict enum 'en' or 'es'
        let validatedLanguage = parsedLanguage;
        if (validatedLanguage !== 'en' && validatedLanguage !== 'es') {
          validatedLanguage = DEFAULT_LANGUAGE;
        }

        // Validate selectedAgencyId against currentUser.accesses
        let validatedAgencyId = parsedSelectedAgencyId;
        if (currentUser && currentUser.accesses) {
          const agencyExists = currentUser.accesses.some(
            (access) => access._id === validatedAgencyId
          );
          if (!agencyExists && validatedAgencyId !== '') {
            validatedAgencyId = DEFAULT_SELECTED_AGENCY_ID;
          }
        }

        // Process recentOrders: migrate lastOpened → lastSeen, coerce to number
        const processedRecentOrders = Array.isArray(parsedRecentOrders)
          ? parsedRecentOrders.map((order) => ({
            ...order,
            lastSeen: Number(order.lastSeen ?? order.lastOpened ?? 0),
          }))
          : DEFAULT_RECENT_ORDERS;

        // Legacy migration (one-time): promote localStorage values when server is null/undefined.
        // Runs unconditionally — writer enqueue has no internal gate, it just batches writes.
        const migrateLegacy = () => {
          let migrated = false;

          // selectedAgencyId ← 'agency_selected_id' (string or JSON object with _id/selectedAgencyId)
          if (prefs.selectedAgencyId == null) {
            try {
              const raw = localStorage.getItem('agency_selected_id');
              if (raw != null && raw !== '') {
                let val = safeJsonParse(raw, '');
                if (val != null && typeof val === 'object' && !Array.isArray(val)) {
                  val = val._id || val.selectedAgencyId || '';
                } else if (typeof val !== 'string') {
                  val = String(val);
                }
                writer.enqueue('selectedAgencyId', val);
                localStorage.removeItem('agency_selected_id');
                migrated = true;
              }
            } catch (_) { /* best-effort */ }
          }

          // recentOrders ← 'tabs' primary, 'v2-workspace' fallback (array of {id,code})
          if (prefs.recentOrders == null) {
            try {
              const rawTabs = localStorage.getItem('tabs');
              let val;
              if (rawTabs != null && rawTabs !== '') {
                val = safeJsonParse(rawTabs, DEFAULT_RECENT_ORDERS);
                localStorage.removeItem('tabs');
              } else {
                // v2-workspace fallback: stores {id,code}[] workspace tabs → recentOrders
                const rawWorkspace = localStorage.getItem('v2-workspace');
                if (rawWorkspace != null && rawWorkspace !== '') {
                  val = safeJsonParse(rawWorkspace, DEFAULT_RECENT_ORDERS);
                  // Normalize to recentOrders shape: {id, orderId:firstPropertyAddress, firstPropertyAddress:'', lastSeen:0, code}
                  if (!Array.isArray(val)) {
                    if (val != null && typeof val === 'object') {
                      val = [val];
                    } else {
                      val = DEFAULT_RECENT_ORDERS;
                    }
                  }
                  val = val.map((tab) => ({
                    id: tab.id || '',
                    order_id: tab.code || '',
                    firstPropertyAddress: '',
                    lastSeen: 0,
                    code: tab.code || '',
                  }));
                  localStorage.removeItem('v2-workspace');
                } else {
                  val = DEFAULT_RECENT_ORDERS;
                }
              }
              if (!Array.isArray(val)) {
                if (val != null && typeof val === 'object') {
                  val = [val];
                } else {
                  val = DEFAULT_RECENT_ORDERS;
                }
              }
              const migratedTabs = val.map((tab) => ({
                ...tab,
                lastSeen: Number(tab.lastSeen ?? tab.lastOpened ?? 0),
              }));
              writer.enqueue('recentOrders', migratedTabs);
              migrated = true;
            } catch (_) { /* best-effort */ }
          }

          // orderColumns ← 'ORDER_COLUMNS' (JSON array)
          if (prefs.orderColumns == null) {
            try {
              const raw = localStorage.getItem('ORDER_COLUMNS');
              if (raw != null && raw !== '') {
                const val = safeJsonParse(raw, DEFAULT_ORDER_COLUMNS);
                writer.enqueue('orderColumns', val);
                localStorage.removeItem('ORDER_COLUMNS');
                migrated = true;
              }
            } catch (_) { /* best-effort */ }
          }

          // recentOrders ← 'tabs' (JSON array with lastOpened → lastSeen mapping)
          if (prefs.recentOrders == null) {
            try {
              const raw = localStorage.getItem('tabs');
              if (raw != null && raw !== '') {
                let val = safeJsonParse(raw, DEFAULT_RECENT_ORDERS);
                if (!Array.isArray(val)) {
                  // Single tab object → wrap in array
                  if (val != null && typeof val === 'object') {
                    val = [val];
                  } else {
                    val = DEFAULT_RECENT_ORDERS;
                  }
                }
                // Map lastOpened → lastSeen, coerce to number
                const migratedTabs = val.map((tab) => ({
                  ...tab,
                  lastSeen: Number(tab.lastSeen ?? tab.lastOpened ?? 0),
                }));
                writer.enqueue('recentOrders', migratedTabs);
                localStorage.removeItem('tabs');
                migrated = true;
              }
            } catch (_) { /* best-effort */ }
          }

          // view-tab-selected ← standalone legacy key (promotes to ordersView if value found)
          if (prefs.ordersView == null) {
            try {
              const raw = localStorage.getItem('view-tab-selected');
              if (raw != null && raw !== '') {
                const val = safeJsonParse(raw, '');
                if (val != null && typeof val === 'object' && !Array.isArray(val)) {
                  writer.enqueue('ordersView', val.selectedTab || '');
                } else if (typeof val === 'string') {
                  writer.enqueue('ordersView', val);
                }
                localStorage.removeItem('view-tab-selected');
                migrated = true;
              }
            } catch (_) { /* best-effort */ }
          }

          return migrated;
        };

        migrateLegacy();
        setSelectedAgencyId(validatedAgencyId);
        setOrdersView(parsedOrdersView);
        setOrderColumns(parsedOrderColumns);
        setRecentOrders(processedRecentOrders);
        setOrderMenuCollapsed(parsedOrderMenuCollapsed);
        setAssistantFullscreen(parsedAssistantFullscreen);
        setLanguage(validatedLanguage);
        setThemeState(validatedTheme);
        setAppTheme(validatedTheme);
        i18n.changeLanguage(validatedLanguage);
        setIsReady(true);
      } catch (err) {
        logger.error('Failed to load preferences:', err);
        setError(err.message || 'Failed to load preferences');
        setIsReady(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      loadPreferences();
    } else {
      setIsReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser]);

  /**
	 * activateOrder: Add or update an order in recentOrders.
	 * Implements ordering invariant:
	 *   - Clones array (non-mutating)
	 *   - Finds existing by id
	 *   - Sets lastSeen to current timestamp
	 *   - Moves active order to index 0
	 *   - Sorts others by lastSeen desc
	 *   - Caps at 10 items
	 */
  const activateOrder = (order) => {
    setRecentOrders((prev) => {
      let updated = [...prev];
      const existingIndex = updated.findIndex((o) => o.id === order.id);
      const now = Date.now();

      if (existingIndex > -1) {
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...order,
          lastSeen: now,
        };
      } else {
        updated.push({
          ...order,
          lastSeen: now,
        });
      }

      const activeOrder = updated.find((o) => o.id === order.id);
      updated = updated.filter((o) => o.id !== order.id);
      updated.sort((a, b) => b.lastSeen - a.lastSeen);
      updated.unshift(activeOrder);

      if (updated.length > 10) {
        updated = updated.slice(0, 10);
      }

      if (isReady) {
        writer.enqueue('recentOrders', updated);
      }

      return updated;
    });
  };

  /**
	 * removeRecentOrder: Remove an order from recentOrders.
	 */
  const removeRecentOrder = (orderId) => {
    setRecentOrders((prev) => {
      const updated = prev.filter((o) => o.id !== orderId);

      // Enqueue write only when ready
      if (isReady) {
        writer.enqueue('recentOrders', updated);
      }

      return updated;
    });
  };

  /**
	 * updateRecentOrderAddress: Update address for an order in recentOrders.
	 */
  const updateRecentOrderAddress = (orderId, address) => {
    setRecentOrders((prev) => {
      const updated = prev.map((o) =>
        o.order_id === orderId ? { ...o, address } : o
      );

      // Enqueue write only when ready
      if (isReady) {
        writer.enqueue('recentOrders', updated);
      }

      return updated;
    });
  };

  // Wrapped setters that enqueue writes when ready
  const handleSetSelectedAgencyId = (value) => {
    setSelectedAgencyId(value);
    if (isReady) {
      writer.enqueue('selectedAgencyId', value);
    }
  };

  const handleSetOrdersView = (value) => {
    setOrdersView(value);
    if (isReady) {
      writer.enqueue('ordersView', value);
    }
  };

  const handleSetOrderColumns = (value) => {
    setOrderColumns(value);
    if (isReady) {
      writer.enqueue('orderColumns', value);
    }
  };

  const handleSetOrderMenuCollapsed = (value) => {
    setOrderMenuCollapsed(value);
    if (isReady) {
      writer.enqueue('orderMenuCollapsed', value);
    }
  };

  const handleSetAssistantFullscreen = (value) => {
    setAssistantFullscreen(value);
    if (isReady) {
      writer.enqueue('assistantFullscreen', value);
    }
  };

  const handleSetLanguage = (value) => {
    const validatedValue = (value === 'en' || value === 'es') ? value : DEFAULT_LANGUAGE;
    setLanguage(validatedValue);
    if (isReady) {
      writer.enqueue('language', validatedValue);
    }
  };

  const handleSetTheme = (value) => {
    const validatedValue = value === 'dark' ? 'dark' : 'light';
    setThemeState(validatedValue);
    setAppTheme(validatedValue);
    if (isReady) {
      writer.enqueue('theme', validatedValue);
    }
  };

  const value = {
    selectedAgencyId,
    ordersView,
    orderColumns,
    recentOrders,
    orderMenuCollapsed,
    assistantFullscreen,
    language,
    theme,
    isReady,
    isLoading,
    error,
    setSelectedAgencyId: handleSetSelectedAgencyId,
    setOrdersView: handleSetOrdersView,
    setOrderColumns: handleSetOrderColumns,
    setOrderMenuCollapsed: handleSetOrderMenuCollapsed,
    setAssistantFullscreen: handleSetAssistantFullscreen,
    setLanguage: handleSetLanguage,
    setTheme: handleSetTheme,
    activateOrder,
    removeRecentOrder,
    updateRecentOrderAddress,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

export default PreferencesContext;
